import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AuditorLayout from "@/components/AuditorLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, FileText, Shield, History, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Use the bundled worker from pdfjs-dist (matches the version react-pdf depends on)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const AuditorDocumentViewer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, fullName } = useAuth();
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number>(800);
  const viewerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Observe container width for responsive page rendering
  useEffect(() => {
    if (!viewerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(Math.max(280, w - 24));
    });
    ro.observe(viewerRef.current);
    return () => ro.disconnect();
  }, []);

  // Track which page is most visible
  const onPageVisible = useCallback((pageNum: number) => setCurrentPage(pageNum), []);

  const scrollToPage = (n: number) => {
    const el = pageRefs.current[n];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const { data: doc, isLoading } = useQuery({
    queryKey: ["auditor-doc", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await (supabase as any).from("operating_documents").select("*").eq("id", id!).maybeSingle();
      return data;
    },
  });
  const { data: revisions = [] } = useQuery({
    queryKey: ["auditor-doc-revisions", id],
    enabled: !!id,
    queryFn: async () => (await (supabase as any).from("document_revisions").select("*").eq("document_id", id).order("revision_date", { ascending: false })).data ?? [],
  });

  // Fetch signed URL + log access
  useEffect(() => {
    if (!doc || !user) return;
    let cancelled = false;
    (async () => {
      if (!doc.file_path) { setAccessDenied(true); return; }
      const { data, error } = await supabase.storage.from("operating-documents").createSignedUrl(doc.file_path, 300);
      if (cancelled) return;
      if (error || !data?.signedUrl) {
        setAccessDenied(true);
        return;
      }
      setSignedUrl(data.signedUrl);
      // immutable access log
      await (supabase as any).from("document_access_log").insert({
        user_id: user.id,
        user_email: user.email,
        document_id: doc.id,
        document_name: doc.name,
        revision_number: doc.revision_number,
        action: "view",
        user_agent: navigator.userAgent,
      });
    })();
    return () => { cancelled = true; };
  }, [doc, user]);

  // Disable context menu & print
  useEffect(() => {
    const onContext = (e: MouseEvent) => e.preventDefault();
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "s")) { e.preventDefault(); toast.error("Disabled for controlled documents"); }
    };
    document.addEventListener("contextmenu", onContext);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  if (isLoading) return <AuditorLayout><p className="text-primary-foreground/50">Loading…</p></AuditorLayout>;

  if (!doc) {
    return (
      <AuditorLayout>
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <Card className="bg-navy-deep border-primary-foreground/10">
          <CardContent className="p-8 text-center">
            <Lock className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-primary-foreground">This document is not in your audit scope.</p>
            <p className="text-sm text-primary-foreground/50 mt-1">If you require access, contact the Designated Person Ashore.</p>
          </CardContent>
        </Card>
      </AuditorLayout>
    );
  }

  const watermark = `${fullName || user?.email || "AUDITOR"} • ${new Date().toISOString().slice(0, 16).replace("T", " ")} UTC • CONTROLLED COPY — VIEW ONLY — NOT FOR DISTRIBUTION`;

  return (
    <AuditorLayout>
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-3 text-primary-foreground/60 hover:text-primary-foreground"><ArrowLeft className="w-4 h-4 mr-1" /> Back to library</Button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        {/* Viewer */}
        <Card className="bg-navy-deep border-primary-foreground/10 overflow-hidden">
          <CardHeader className="border-b border-primary-foreground/10">
            <CardTitle className="text-primary-foreground text-base flex items-center gap-2">
              {doc.is_restricted && <Lock className="w-4 h-4 text-amber-400" />}
              <FileText className="w-4 h-4 text-teal" />
              <span className="truncate">{doc.control_number ? `${doc.control_number} · ` : ""}{doc.name}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Toolbar */}
            {signedUrl && !accessDenied && (
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-primary-foreground/10 bg-navy-deep/60">
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => scrollToPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1}><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-xs text-primary-foreground/70 font-mono min-w-[70px] text-center">
                    {currentPage} / {numPages || "—"}
                  </span>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => scrollToPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages}><ChevronRight className="w-4 h-4" /></Button>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setScale((s) => Math.max(0.5, +(s - 0.1).toFixed(2)))}><ZoomOut className="w-4 h-4" /></Button>
                  <span className="text-xs text-primary-foreground/60 font-mono min-w-[44px] text-center">{Math.round(scale * 100)}%</span>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setScale((s) => Math.min(2.5, +(s + 0.1).toFixed(2)))}><ZoomIn className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
            <div ref={viewerRef} className="relative bg-black/40 overflow-y-auto" style={{ height: "78vh" }}>
              {accessDenied && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-primary-foreground/60 p-6 text-center">
                  <Lock className="w-8 h-8 text-amber-400" />
                  <p>Unable to retrieve this controlled document.</p>
                  <p className="text-xs text-primary-foreground/40">The underlying file is missing or you do not have a grant for this restricted document.</p>
                </div>
              )}
              {signedUrl && (
                <Document
                  file={signedUrl}
                  onLoadSuccess={({ numPages }) => { setNumPages(numPages); setCurrentPage(1); }}
                  onLoadError={(err) => { console.error("PDF load error", err); toast.error("Failed to load document"); }}
                  loading={<div className="flex items-center justify-center h-full text-primary-foreground/50 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading document…</div>}
                  error={<div className="flex items-center justify-center h-full text-amber-400 text-sm">Unable to render this PDF.</div>}
                  className="flex flex-col items-center gap-3 py-3"
                >
                  {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
                    <PageWrapper key={p} pageNumber={p} width={containerWidth * scale} onVisible={onPageVisible} pageRefs={pageRefs} watermark={watermark} />
                  ))}
                </Document>
              )}
            </div>
            <div className="p-3 text-[10px] text-primary-foreground/40 border-t border-primary-foreground/10 flex items-center gap-2">
              <Shield className="w-3 h-3" />
              Download and print are disabled. This view is recorded in the company's document access log.
            </div>
          </CardContent>
        </Card>


        {/* Metadata */}
        <div className="space-y-3">
          <Card className="bg-navy-deep border-primary-foreground/10">
            <CardHeader><CardTitle className="text-primary-foreground text-sm">Control Metadata</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-xs">
              {[
                ["Control Number", doc.control_number],
                ["Framework", doc.framework],
                ["Reference", doc.framework_ref],
                ["Category", doc.category],
                ["Applicability", doc.applicability],
                ["Vessel", doc.vessel_name, doc.vessel_imo ? `IMO ${doc.vessel_imo}` : null],
                ["Revision", doc.revision_number],
                ["Revision Date", doc.revision_date],
                ["Effective", doc.effective_date],
                ["Next Review", doc.next_review_date],
                ["Owner", doc.document_owner],
                ["Approved By", doc.approved_by],
              ].map(([label, value, extra]: any) => value ? (
                <div key={label} className="flex justify-between gap-3">
                  <span className="text-primary-foreground/40">{label}</span>
                  <span className="text-primary-foreground/80 text-right">{value}{extra ? ` · ${extra}` : ""}</span>
                </div>
              ) : null)}
              <div className="pt-2 border-t border-primary-foreground/10 flex justify-between">
                <span className="text-primary-foreground/40">Status</span>
                <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">{doc.doc_status || "current"}</Badge>
              </div>
              {doc.change_summary && (
                <div className="pt-2 border-t border-primary-foreground/10">
                  <p className="text-primary-foreground/40 mb-1">Change summary</p>
                  <p className="text-primary-foreground/80">{doc.change_summary}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-navy-deep border-primary-foreground/10">
            <CardHeader><CardTitle className="text-primary-foreground text-sm flex items-center gap-2"><History className="w-3.5 h-3.5 text-teal" /> Revision History</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="flex justify-between p-2 rounded bg-teal/5 border border-teal/20">
                <span className="text-teal font-mono">Rev {doc.revision_number || "—"}</span>
                <span className="text-primary-foreground/60">{doc.revision_date || "—"} · current</span>
              </div>
              {(revisions as any[]).map((r) => (
                <div key={r.id} className="p-2 rounded bg-primary-foreground/5">
                  <div className="flex justify-between">
                    <span className="font-mono text-primary-foreground/70">Rev {r.revision_number}</span>
                    <span className="text-primary-foreground/50">{r.revision_date}</span>
                  </div>
                  {r.change_summary && <p className="text-primary-foreground/60 mt-1">{r.change_summary}</p>}
                </div>
              ))}
              {revisions.length === 0 && <p className="text-primary-foreground/40">No prior revisions recorded.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </AuditorLayout>
  );
};

interface PageWrapperProps {
  pageNumber: number;
  width: number;
  onVisible: (n: number) => void;
  pageRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  watermark: string;
}

const PageWrapper = ({ pageNumber, width, onVisible, pageRefs, watermark }: PageWrapperProps) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    pageRefs.current[pageNumber] = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.5) onVisible(pageNumber);
        });
      },
      { threshold: [0.5] },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [pageNumber, onVisible, pageRefs]);

  return (
    <div ref={ref} className="relative shadow-lg" data-page={pageNumber}>
      <Page pageNumber={pageNumber} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
      <div className="absolute top-1 right-2 text-[9px] font-mono text-primary-foreground/30 bg-black/40 px-1.5 py-0.5 rounded">
        p.{pageNumber}
      </div>
      <div className="pointer-events-none absolute inset-0 overflow-hidden select-none" aria-hidden="true">
        <div className="absolute inset-0 flex flex-wrap content-around justify-around opacity-[0.10] rotate-[-28deg] origin-center">
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="text-[10px] font-mono text-foreground whitespace-nowrap px-4 py-2">
              {watermark}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuditorDocumentViewer;
